import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import React, {useMemo, useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {Textbox} from 'src/design/atoms/Textbox';
import type {IStepItem} from 'src/design/molecules/VerticalLinearStepper';
import {VerticalLinearStepper} from 'src/design/molecules/VerticalLinearStepper';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {SidebarLinkInstances} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useDeleteInstanceMutation,
  useGetInstanceQuery,
} from 'src/redux/queries/instance';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {instanceDocumentTitle} from '../instanceHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

interface DeleteInstanceFormValues {
  confirmationName: string;
}

export const DeleteInstancePage: React.FC = () => {
  useSetDocumentTitle(instanceDocumentTitle);
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();

  const {id} = useParams();

  const {control} = useForm<DeleteInstanceFormValues>({
    defaultValues: {confirmationName: ''},
  });
  const confirmationName = useWatch({control, name: 'confirmationName'});

  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const {data: item} = useGetInstanceQuery(Number(id), {
    skip: id === undefined,
  });

  const [deleteInstance] = useDeleteInstanceMutation();

  const isNameConfirmed =
    item?.name !== undefined && confirmationName.trim() === item.name;

  const handleCompletedSteps = async () => {
    try {
      await deleteInstance({
        instanceId: Number(id),
        confirmationName: confirmationName.trim(),
      }).unwrap();

      setAlert('Instance was successfully deleted', 'success');
      navigate(PathUtility.Instances());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const steps: IStepItem[] = [
    {
      label: 'Review Deletion',
      component: (
        <Box mb="lg">
          <p>
            You have initiated the deletion process for the <b>{item?.name}</b>{' '}
            instance.
          </p>
          <p>
            Deleting this instance hides the instance and all of its data from
            all users. Only the instance named <b>{item?.name}</b> is affected.
          </p>
          <Alert color="red" title="This action cannot be undone from the UI">
            Once deleted, the instance and its data can no longer be accessed
            through the application.
          </Alert>
        </Box>
      ),
    },
    {
      label: 'Acknowledge Consequences',
      handleIsContinueDisabled: () => !isAcknowledged,
      component: (
        <Box>
          <p>
            Before continuing, please review and acknowledge the consequences of
            deleting the <b>{item?.name}</b> instance:
          </p>
          <ul>
            <li>All users of this instance will lose access.</li>
            <li>The instance's data will no longer be visible to anyone.</li>
            <li>Integrations configured for this instance will stop.</li>
          </ul>
          <CheckboxRow
            checked={isAcknowledged}
            label="I understand these consequences and want to continue."
            onChange={(_, checked) => setIsAcknowledged(checked)}
          />
        </Box>
      ),
    },
    {
      label: 'Confirm Instance Name',
      handleIsContinueDisabled: () => !isNameConfirmed,
      component: (
        <Box mb="lg">
          <p>
            To confirm deletion, type the instance name <b>{item?.name}</b>{' '}
            exactly as shown below. This helps prevent accidental deletions.
          </p>
          <Controller
            name="confirmationName"
            control={control}
            render={({field}) => (
              <Textbox
                label="Instance Name"
                value={field.value}
                onChangeCallback={field.onChange}
                onBlurCallback={field.onBlur}
              />
            )}
          />
        </Box>
      ),
    },
  ];

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const crumbs: BreadcrumbItem[] = [
      {label: 'Instances', to: PathUtility.Instances()},
    ];
    if (item?.name) {
      crumbs.push({
        label: item.name,
        to: PathUtility.InstancesView(String(id), 'overview'),
      });
    }
    crumbs.push({label: 'Delete'});
    return crumbs;
  }, [id, item?.name]);

  const fallbackTo = PathUtility.InstancesView(String(id), 'overview');

  return (
    <ContentWrapper>
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkInstances.icon}>
          {item ? `Delete ${item?.name}` : 'Delete Instance'}
        </PageHeader.Title>
        {item?.internalIdentifier && (
          <PageHeader.Meta>{item.internalIdentifier}</PageHeader.Meta>
        )}
      </PageHeader>
      <Box mt="md">
        <VerticalLinearStepper
          steps={steps}
          onCompletedSteps={handleCompletedSteps}
        />
      </Box>
    </ContentWrapper>
  );
};

export default DeleteInstancePage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.InstancesDelete(':id'),
  element: <DeleteInstancePage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
